import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function HoodiesGuide() {
  return (
    <>
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Sugargoo Hoodies and Sweaters Guide 2026: Streetwear Essentials',
          description: 'Complete guide to buying hoodies, sweaters, and streetwear on Sugargoo. Learn about sizing, materials, quality tiers, and the best community-reviewed apparel finds.',
          datePublished: '2026-03-10',
          dateModified: '2026-08-06',
          author: { '@type': 'Organization', name: 'Sugargoo Sheet Help' },
          publisher: { '@type': 'Organization', name: 'Sugargoo Sheet Help' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sugargoosheet.help/sugargoo-hoodies-sweaters-guide' },
        }}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sugargoosheet.help/' },
            { '@type': 'ListItem', position: 2, name: 'Sugargoo Hoodies & Sweaters Guide 2026', item: 'https://sugargoosheet.help/sugargoo-hoodies-sweaters-guide' },
          ],
        }}
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'How do I choose the right size for Sugargoo hoodies?', acceptedAnswer: { '@type': 'Answer', text: 'Always check the seller-specific size chart and compare measurements to clothing you already own. Asian sizing runs about one size smaller than Western sizing.' } },
            { '@type': 'Question', name: 'What GSM weight is best for hoodies?', acceptedAnswer: { '@type': 'Answer', text: 'For durable, warm hoodies, look for 350-450 GSM. Lightweight hoodies (200-300 GSM) are fine for layering or mild weather.' } },
            { '@type': 'Question', name: 'Should I remove tags before shipping apparel?', acceptedAnswer: { '@type': 'Answer', text: 'Removing tags can reduce weight slightly, but the savings are minimal. Keep tags if you value original presentation or may resell later.' } },
            { '@type': 'Question', name: 'How do I check print quality in QC photos?', acceptedAnswer: { '@type': 'Answer', text: 'Look for clean edges, consistent color saturation, and proper alignment. Blurry edges, color bleeding, or misaligned prints are red flags.' } },
          ],
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article className="pt-24 md:pt-28 pb-16 md:pb-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-1.5 text-xs text-foreground-500 font-label mb-6 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-700">Sugargoo Hoodies Guide</span>
              </nav>

              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium">Apparel</span>
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium">2026 Edition</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  Sugargoo Hoodies &amp; Sweaters Guide 2026: Streetwear Essentials
                </h1>
                <p className="text-lg text-foreground-600 leading-relaxed max-w-3xl">
                  Hoodies and sweaters are staple pieces in every wardrobe. This guide covers everything you need to know about buying quality apparel on Sugargoo, from fabric types and sizing to the best community-verified finds in the Spreadsheet database.
                </p>
              </div>

              <div className="aspect-[21/9] w-full rounded-2xl overflow-hidden bg-background-200 mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20folded%20hoodies%20and%20sweaters%20stacked%20neatly%20on%20a%20warm%20wooden%20table%2C%20neutral%20cream%20and%20earth%20tone%20streetwear%20essentials%2C%20soft%20natural%20lighting%20from%20a%20window%2C%20editorial%20fashion%20photography%20with%20minimal%20clean%20composition&width=1200&height=514&seq=hoodies-hero-2026&orientation=landscape"
                  alt="Sugargoo Hoodies and Sweaters Guide 2026 - curated streetwear apparel collection"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
                <div className="flex-1">
                  <div className="prose-custom">
                    <h2>Apparel Shopping on Sugargoo: What to Know</h2>
                    <p>
                      Hoodies, sweaters, and other apparel represent one of the most reliable categories on Sugargoo. Unlike items with complex technical specifications, clothing quality is relatively straightforward to assess through QC photos. Fabric weight, stitching patterns, print quality, and overall construction are visible and verifiable before you commit to international shipping.
                    </p>

                    <h2>Understanding Fabric Types and Weights</h2>
                    <p>
                      Fabric quality is the single most important factor in apparel satisfaction. Here is what to look for: Cotton weight is measured in grams per square meter (GSM). Lightweight hoodies range from 200-300 GSM, mid-weight from 300-400 GSM, and heavyweight from 400-500+ GSM. For durable, warm hoodies suitable for cooler weather, aim for 350 GSM or higher. French terry and fleece-lined interiors provide additional warmth and comfort. Check QC photos for fabric texture and interior lining details.
                    </p>

                    <h3>Common Materials</h3>
                    <p>
                      <strong>Cotton:</strong> Breathable, soft, and durable. Look for 100% cotton or cotton-polyester blends for everyday hoodies. <strong>Polyester blends:</strong> Provide stretch and shape retention, common in athletic and performance wear. <strong>Fleece-lined:</strong> Soft interior lining for warmth, ideal for winter pieces. <strong>French terry:</strong> Smooth exterior with looped interior, premium casual feel.
                    </p>

                    <h2>Sizing for Hoodies and Sweaters</h2>
                    <p>
                      Sizing on Sugargoo apparel requires careful attention. Asian sizing typically runs smaller than Western equivalents by approximately one full size. A "Large" in Chinese sizing often corresponds to a Western "Medium." Always consult the seller's size chart, which lists measurements in centimeters for chest width, shoulder width, sleeve length, and overall garment length. When between sizes, size up for a relaxed fit or stick to the smaller size for a fitted look.
                    </p>

                    <div className="overflow-x-auto rounded-xl border border-background-200/70 my-6">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-background-100">
                            <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Asian Size</th>
                            <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Western Equivalent</th>
                            <th className="text-left px-4 py-3 font-label font-semibold text-foreground-800 text-xs uppercase tracking-wider">Chest (cm)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">M</td>
                            <td className="px-4 py-3 text-foreground-600">XS-S</td>
                            <td className="px-4 py-3 text-foreground-600">100-108</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">L</td>
                            <td className="px-4 py-3 text-foreground-600">S-M</td>
                            <td className="px-4 py-3 text-foreground-600">108-116</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">XL</td>
                            <td className="px-4 py-3 text-foreground-600">M-L</td>
                            <td className="px-4 py-3 text-foreground-600">116-124</td>
                          </tr>
                          <tr className="border-t border-background-200/50">
                            <td className="px-4 py-3 text-foreground-800 font-medium">XXL</td>
                            <td className="px-4 py-3 text-foreground-600">L-XL</td>
                            <td className="px-4 py-3 text-foreground-600">124-132</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <h2>Quality Checkpoints for Apparel</h2>
                    <p>
                      When reviewing QC photos for hoodies and sweaters, examine these key quality indicators: stitching density and straightness along seams, especially at shoulders and side seams; print or embroidery quality and alignment; fabric texture consistency; ribbed cuff and hem elasticity; drawstring quality and metal aglet finishing; and interior lining or fleece quality. Premium pieces will show clean finishing at all these points, while budget options may have minor inconsistencies that are acceptable for the price.
                    </p>

                    <h2>Popular Hoodie and Sweater Styles</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                      {[
                        { name: 'Pullover Hoodies', desc: 'Classic over-the-head style with kangaroo pocket' },
                        { name: 'Zip-Up Hoodies', desc: 'Front zipper closure for versatile layering' },
                        { name: 'Crewneck Sweaters', desc: 'Minimal collarless design for clean looks' },
                        { name: 'Oversized Hoodies', desc: 'Relaxed fit for modern streetwear aesthetics' },
                        { name: 'Turtleneck Sweaters', desc: 'High collar style for warmth and elegance' },
                        { name: 'Cardigans', desc: 'Button-front layering piece for versatile outfits' },
                      ].map((item, i) => (
                        <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.name}</h4>
                          <p className="text-sm text-foreground-600">{item.desc}</p>
                        </div>
                      ))}
                    </div>

                    <div className="cta-card">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-2">Browse Verified Apparel Finds</h3>
                          <p className="text-sm text-foreground-600">Discover quality hoodies, sweaters, and streetwear in the Sugargoo Spreadsheet.</p>
                        </div>
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                        >
                          Sugargoo Spreadsheet
                          <i className="ri-arrow-right-up-line"></i>
                        </a>
                      </div>
                    </div>

                    <h2>Apparel FAQ</h2>

                    <div className="space-y-4 my-6">
                      {[
                        {
                          q: 'How do I choose the right size for Sugargoo hoodies?',
                          a: 'Always check the seller-specific size chart and compare measurements to clothing you already own. Asian sizing runs about one size smaller than Western sizing.',
                        },
                        {
                          q: 'What GSM weight is best for hoodies?',
                          a: 'For durable, warm hoodies, look for 350-450 GSM. Lightweight hoodies (200-300 GSM) are fine for layering or mild weather.',
                        },
                        {
                          q: 'Should I remove tags before shipping apparel?',
                          a: 'Removing tags can reduce weight slightly, but the savings are minimal. Keep tags if you value original presentation or may resell later.',
                        },
                        {
                          q: 'How do I check print quality in QC photos?',
                          a: 'Look for clean edges, consistent color saturation, and proper alignment. Blurry edges, color bleeding, or misaligned prints are red flags.',
                        },
                      ].map((faq, i) => (
                        <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{faq.q}</h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-10 p-6 md:p-8 bg-background-100 border border-background-200/70 rounded-2xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-2">Upgrade Your Wardrobe</h3>
                        <p className="text-sm text-foreground-600">Find the best hoodies, sweaters, and streetwear in the Sugargoo Spreadsheet.</p>
                      </div>
                      <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-label font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                      >
                        Buy It Now
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-background-200/70 flex flex-wrap gap-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                    >
                      <i className="ri-arrow-left-line"></i>Home
                    </Link>
                    <Link to="/sugargoo-spreadsheet-guide" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                    >
                      Spreadsheet Guide
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>

                <aside className="lg:w-72 flex-shrink-0">
                  <div className="sticky top-24 space-y-6">
                    <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                      <h4 className="font-label text-sm font-semibold text-foreground-900 mb-3 uppercase tracking-wider">Quick Links</h4>
                      <div className="space-y-2.5">
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                        >
                          <i className="ri-external-link-line"></i>Sugargoo Spreadsheet
                        </a>
                        <Link to="/" className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-label font-medium transition-colors"
                        >
                          <i className="ri-home-line"></i>Homepage
                        </Link>
                      </div>
                    </div>
                    <div className="bg-accent-100 border border-accent-200/50 rounded-xl p-5">
                      <h4 className="font-label text-sm font-semibold text-accent-800 mb-3 uppercase tracking-wider">Related Guides</h4>
                      <div className="space-y-2.5">
                        <Link to="/sugargoo-spreadsheet-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Spreadsheet Guide</Link>
                        <Link to="/best-sugargoo-shoes" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Shoes Guide</Link>
                        <Link to="/sugargoo-accessories-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Accessories Guide</Link>
                        <Link to="/sugargoo-warehouse-guide" className="block text-sm text-accent-700 hover:text-accent-800 transition-colors">Warehouse Guide</Link>
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}